'use client'

import { motion } from 'framer-motion'
import { useAtom, useAtomValue, useSetAtom, useStore } from 'jotai'
import { ActivityIcon, BookOpenIcon, LogOutIcon, SquarePenIcon } from 'lucide-react'
import { useTranslations } from 'next-intl'
import Link from 'next/link'
import { usePathname, useRouter } from 'next/navigation'
import { useCallback, useEffect, useState, useSyncExternalStore } from 'react'
import { createPortal } from 'react-dom'

import SettingDialog from '@/components/dialog/settings/setting-dialog'
import {
  AppSidePanel,
  type AppSidePanelItem,
  RAIL_ACTIVE_SPRING,
  RailTooltip,
} from '@/components/navigation/app-side-panel'
import {
  ArbitrageIcon,
  CandlesIcon,
  HomeIcon,
  LayersIcon,
  RadarIcon,
  RfqIcon,
  SettingsRingIcon,
  SparkleIcon,
  TrendIcon,
} from '@/components/shell-icons'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  AssistantRecentList,
  useHasUnreadResearch,
} from '@/features/assistant/components/assistant-recent-list'
import { useAuth } from '@/features/auth/hooks'
import { useAuthGuard } from '@/features/auth/hooks/use-auth-guard'
import { PlanBadge } from '@/features/billing/components/plan-badge'
import {
  SidePanelConnectionStatus,
  SidePanelSupport,
} from '@/features/dashboard/components/side-panel-status'
import { useNavigateToTradePage } from '@/features/dashboard/hooks/use-navigate-to-trade-page'
import {
  getArbitragePagePath,
  getAssistantPagePath,
  getHedgePagePath,
  getHomePagePath,
  getOptionsMmPagePath,
  getRfqMakerPagePath,
  getRiskSlidesPagePath,
  getScreenerPagePath,
  getTradePagePath,
  isArbitragePagePath,
  isAssistantPagePath,
  isHedgePagePath,
  isHomePagePath,
  isOptionsMmPagePath,
  isRfqMakerPagePath,
  isRiskSlidesPagePath,
  isScreenerPagePath,
} from '@/features/dashboard/lib/trade-page-path'
import { openAssistant } from '@/features/order/chat/lib/open-assistant'
import { TutorialProgressRing } from '@/features/tutorial/components/tutorial-progress-ring'
import { useTutorialCompletion } from '@/features/tutorial/hooks/use-tutorial-completion'
import { useCapabilities } from '@/hooks/use-capabilities'
import { useIsMobileLayout } from '@/hooks/use-screen'
import { SettingTab } from '@/lib/configs/setting-config'
import { cn } from '@/lib/utils'
import { prefetchFullRoute } from '@/lib/utils/prefetch-route'
import { useLogout } from '@/service/session.hook'
import { useGetPages } from '@/service/ui-state.hook'
import { activePageIdAtom } from '@/state/dashboard-layouts'
import { tradeSidePanelCollapsedAtom } from '@/state/trade-side-panel'
import { tutorialHubOpenAtom } from '@/state/tutorial'
import { AlgoType } from '@/types/generated/algo'

const subscribeToBrowser = () => () => {}
/** Viewports wide enough for the assistant page to open with the side panel expanded. */
const AUTO_OPEN_PANEL_QUERY = '(min-width: 1280px)'
// The footer's Tutorials and Settings rows (Figma "Rail item": 36px, radius sm).
const FOOTER_BUTTON_COLLAPSED =
  'hover:bg-foreground/5 flex size-9 cursor-pointer items-center justify-center rounded-sm transition-[background-color,scale] duration-150 motion-safe:active:scale-[0.94]'
const FOOTER_BUTTON_EXPANDED =
  'hover:bg-foreground/5 flex h-9 w-full cursor-pointer items-center gap-2 rounded-sm pr-3 pl-2.5 text-sm font-medium transition-[background-color,scale] duration-150 motion-safe:active:scale-[0.97]'
const PRIMARY_NAV_ITEM_CLASS =
  'relative text-orderx-button-mint hover:bg-foreground/5 flex h-9 w-full shrink-0 cursor-pointer items-center gap-2 rounded-sm pr-3 pl-2.5 text-sm font-medium tracking-tight transition-[color,background-color,scale] duration-150 motion-safe:active:scale-[0.97]'
const EXPANDED_ACTIVE_FILL_ID = 'trade-panel-expanded-active'

type TradeNavId =
  | 'home'
  | 'trade'
  | 'risk-slides'
  | 'delta-hedge'
  | 'screener'
  | 'arbitrage'
  | 'rfq'
  | 'options-mm'
  | 'assistant'

// Prefetched in full on mount (see prefetchFullRoute) and again on hover/focus: the side
// panel's buttons aren't <Link>s, so nothing else warms these, and a plain prefetch of these
// dynamic routes only reached their loading skeleton — every click still waited on the server.
const NAV_PATHS: Partial<Record<TradeNavId, string>> = {
  home: getHomePagePath(),
  'risk-slides': getRiskSlidesPagePath(),
  'delta-hedge': getHedgePagePath(),
  screener: getScreenerPagePath(),
  arbitrage: getArbitragePagePath(),
  rfq: getRfqMakerPagePath(),
  'options-mm': getOptionsMmPagePath(),
  assistant: getAssistantPagePath(),
}

/** "enes.aktas@…" → "EA"; a single-word address takes its first two letters. */
function initialsFromEmail(email: string | undefined): string {
  const local = email?.split('@')[0] ?? ''
  const words = local.split(/[._+-]+/).filter(Boolean)
  const letters = words.length >= 2 ? `${words[0][0]}${words[1][0]}` : local.slice(0, 2)
  return letters.toUpperCase() || '·'
}

/** RailTooltip around `children` only while the rail is collapsed (its labels are hidden). */
function MaybeRailTooltip({
  label,
  enabled,
  children,
}: {
  label: string
  enabled: boolean
  children: React.ReactElement
}) {
  return enabled ? <RailTooltip label={label}>{children}</RailTooltip> : children
}

/** The account avatar (Figma "Avatar"): a 28px trading-buy disc with the user's initials. */
function AccountAvatar({ email }: { email: string | undefined }) {
  return (
    <span
      aria-hidden="true"
      className="bg-chart-mint text-ox-ink flex size-7 shrink-0 items-center justify-center rounded-full text-[10px] leading-[14px] font-medium tracking-[-0.02em]"
    >
      {initialsFromEmail(email)}
    </span>
  )
}

/** The expanded nav's active row fill, sliding between rows like the rail's. */
function ExpandedActiveFill() {
  return (
    <motion.span
      layoutId={EXPANDED_ACTIVE_FILL_ID}
      transition={RAIL_ACTIVE_SPRING}
      aria-hidden="true"
      className="bg-emphasis absolute inset-0 rounded-sm"
    />
  )
}

interface SidebarAccountMenuProps {
  collapsed: boolean
  email: string | undefined
  onLogout: () => void
}

/**
 * The sidebar's account entry: an avatar (collapsed) or the email row (expanded) opening a menu
 * with just the signed-in email and Log out; Settings keeps its own button right above. Log out
 * used to be a one-click icon in the rail's bottom corner, directly under the identical Settings
 * button, so a slightly low click revoked the session. Behind a menu it takes two deliberate
 * clicks, and a stray one only opens something Esc closes.
 */
function SidebarAccountMenu({ collapsed, email, onLogout }: SidebarAccountMenuProps) {
  const t = useTranslations('trade.sidePanel')
  const label = email ?? t('account')

  return (
    <div className={cn('flex items-center', collapsed ? 'justify-center' : 'h-9 w-full gap-2')}>
      <DropdownMenu>
        <MaybeRailTooltip label={label} enabled={collapsed}>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              aria-label={t('accountMenu')}
              className={
                collapsed
                  ? 'hover:ring-orderx-button-mint/40 data-[state=open]:ring-orderx-button-mint/60 flex size-7 shrink-0 cursor-pointer items-center justify-center rounded-full ring-2 ring-transparent transition-[box-shadow,scale] duration-150 motion-safe:active:scale-[0.94]'
                  : 'hover:bg-orderx-button-mint/8 data-[state=open]:bg-orderx-button-mint/8 flex h-9 min-w-0 flex-1 cursor-pointer items-center gap-2 rounded-sm pr-3 pl-1 text-left text-sm font-medium tracking-tight transition-colors'
              }
            >
              <AccountAvatar email={email} />
              {!collapsed && <span className="truncate">{label}</span>}
            </button>
          </DropdownMenuTrigger>
        </MaybeRailTooltip>
        <DropdownMenuContent
          side={collapsed ? 'right' : 'top'}
          align={collapsed ? 'end' : 'start'}
          sideOffset={4}
          className="z-[70] min-w-56"
        >
          <div className="flex items-center gap-2 px-3 py-2">
            <span className="text-foreground/60 min-w-0 flex-1 truncate text-xs">{label}</span>
            {collapsed && <PlanBadge />}
          </div>
          <DropdownMenuSeparator />
          <DropdownMenuItem onSelect={onLogout}>
            <LogOutIcon className="size-4 shrink-0" />
            {t('logOut')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      {!collapsed && <PlanBadge />}
    </div>
  )
}

export function TradeSidePanel() {
  const tNav = useTranslations('trade.sidePanel')
  const { isAlgoAllowed } = useCapabilities()
  const canRfq = isAlgoAllowed(AlgoType.ALGO_TYPE_RFQ)
  const showOptionsMm = isAlgoAllowed(AlgoType.ALGO_TYPE_OPTIONS_MM)
  const tTutorialHub = useTranslations('tutorial.hub')
  const setTutorialHubOpen = useSetAtom(tutorialHubOpenAtom)
  const tutorials = useTutorialCompletion()
  const tutorialPercent = Math.round((tutorials.done / tutorials.total) * 100)
  const tutorialsLabel = tTutorialHub('openWithProgress', { percent: tutorialPercent })
  const [collapsed, setCollapsed] = useAtom(tradeSidePanelCollapsedAtom)
  const tAssistant = useTranslations('trade.assistant')
  const [settingOpen, setSettingOpen] = useState(false)
  const store = useStore()
  // The nav flags a research report waiting to be read; on the assistant page the panel also
  // carries New chat and the recent list (see expandedContent).
  const hasUnreadResearch = useHasUnreadResearch()
  const assistantIndicator = hasUnreadResearch ? tAssistant('unread') : undefined
  const canUseBrowser = useSyncExternalStore(
    subscribeToBrowser,
    () => true,
    () => false
  )
  // Hidden exactly when the header swaps to the mobile hamburger (useIsMobileLayout) — a
  // mobile device wider than that (iPad landscape) keeps the desktop header, so it needs this
  // panel. A narrow desktop window is never the mobile layout: the panel collapses to its icon
  // rail (AppSidePanel's `collapsed` state) rather than vanishing because the browser got resized.
  const isMobileLayout = useIsMobileLayout()
  const router = useRouter()
  const navigateToTradePage = useNavigateToTradePage()
  const pathname = usePathname()
  const isHomePage = isHomePagePath(pathname)
  const isRiskSlidesPage = isRiskSlidesPagePath(pathname)
  const isHedgePage = isHedgePagePath(pathname)
  const isScreenerPage = isScreenerPagePath(pathname)
  const isArbitragePage = isArbitragePagePath(pathname)
  const isRfqMakerPage = isRfqMakerPagePath(pathname)
  const isOptionsMmPage = isOptionsMmPagePath(pathname)
  const isAssistantPage = isAssistantPagePath(pathname)
  const isFullPageSubroute =
    isHomePage ||
    isRiskSlidesPage ||
    isHedgePage ||
    isScreenerPage ||
    isArbitragePage ||
    isRfqMakerPage ||
    isOptionsMmPage ||
    isAssistantPage
  const currentNavId: TradeNavId = isHomePage
    ? 'home'
    : isRiskSlidesPage
      ? 'risk-slides'
      : isHedgePage
        ? 'delta-hedge'
        : isScreenerPage
          ? 'screener'
          : isArbitragePage
            ? 'arbitrage'
            : isRfqMakerPage
              ? 'rfq'
              : isOptionsMmPage
                ? 'options-mm'
                : isAssistantPage
                  ? 'assistant'
                  : 'trade'
  // The pathname only changes once the next route commits, so a highlight driven by it alone
  // lags the click by the whole navigation. The clicked item lights up right away instead, and
  // the pending id is dropped as soon as the pathname moves (including back/forward).
  const [pendingNavId, setPendingNavId] = useState<TradeNavId | null>(null)
  const [pendingFromPathname, setPendingFromPathname] = useState(pathname)
  if (pendingFromPathname !== pathname) {
    setPendingFromPathname(pathname)
    setPendingNavId(null)
  }
  const activeNavId = pendingNavId ?? currentNavId
  const { user, logout } = useAuth()
  const { isLoggedIn } = useAuthGuard()
  const { mutateAsync: logoutSession } = useLogout()
  const handleLogout = useCallback(async () => {
    // Best-effort server-side revocation of this device's session row; the
    // existing logout() flow still clears the httpOnly refresh cookie via
    // DELETE /api/auth/session even if this RPC fails. Mirrors
    // devices-panel.tsx's handleLogout.
    try {
      await logoutSession()
    } catch {
      // ignore — local logout must proceed regardless
    }
    logout()
  }, [logoutSession, logout])
  const { data: pagesData } = useGetPages({ enabled: isLoggedIn })
  const activePageId = useAtomValue(activePageIdAtom)

  // Both are real, page-independent routes (see app/(dashboard)/trade/risk-slides/
  // and .../hedge/page.tsx), not a dialog/overlay — refresh, back/forward, and
  // deep-linking all work on them. (Other hedge-builder entry points — the order
  // form dropdown, Active tab's Hedges header — still open it as a dialog via
  // useOpenHedgeDialog, since those are quick contextual actions on an existing
  // position rather than this sidebar's own dedicated nav item.)
  const openHome = useCallback(() => {
    setPendingNavId('home')
    setCollapsed(true)
    router.push(getHomePagePath())
  }, [router, setCollapsed])

  const openRiskSlides = useCallback(() => {
    setPendingNavId('risk-slides')
    setCollapsed(true)
    router.push(getRiskSlidesPagePath())
  }, [router, setCollapsed])

  const openDeltaHedge = useCallback(() => {
    setPendingNavId('delta-hedge')
    setCollapsed(true)
    router.push(getHedgePagePath())
  }, [router, setCollapsed])

  const openScreener = useCallback(() => {
    setPendingNavId('screener')
    setCollapsed(true)
    router.push(getScreenerPagePath())
  }, [router, setCollapsed])

  const openArbitrage = useCallback(() => {
    setPendingNavId('arbitrage')
    setCollapsed(true)
    router.push(getArbitragePagePath())
  }, [router, setCollapsed])

  const openRfqMaker = useCallback(() => {
    setPendingNavId('rfq')
    setCollapsed(true)
    router.push(getRfqMakerPagePath())
  }, [router, setCollapsed])

  const openOptionsMm = useCallback(() => {
    setPendingNavId('options-mm')
    setCollapsed(true)
    router.push(getOptionsMmPagePath())
  }, [router, setCollapsed])

  // No collapse here: the effect below opens the panel on the assistant page.
  const openAssistantPage = useCallback(() => {
    setPendingNavId('assistant')
    router.push(getAssistantPagePath())
  }, [router])

  // The assistant page's chat history lives in this panel (Gemini-style), so the panel opens
  // on entering the page (link, back/forward or a direct load) and folds back to the rail on
  // leaving it; every other page starts as the compact rail. Either can still be opened or
  // closed from the logo / collapse button. exchange-tab-group.tsx reserves the open width on
  // the assistant page instead of overlaying.
  useEffect(() => {
    setCollapsed(!isAssistantPage)
  }, [isAssistantPage, setCollapsed])

  const tradeTargetPageId =
    activePageId ??
    pagesData?.pages.find((page) => page.isActive)?.pageId ??
    pagesData?.pages[0]?.pageId

  // Where each item navigates. Trade is only a real navigation from a full-page subroute —
  // between dashboard pages it's a client-side pushState (useNavigateToTradePage).
  const pathForNav = useCallback(
    (id: TradeNavId): string | undefined =>
      id === 'trade'
        ? isFullPageSubroute && tradeTargetPageId
          ? getTradePagePath(tradeTargetPageId)
          : undefined
        : NAV_PATHS[id],
    [isFullPageSubroute, tradeTargetPageId]
  )
  const warmNav = useCallback(
    (id: TradeNavId) => {
      const path = pathForNav(id)
      if (isLoggedIn && path && path !== pathname) prefetchFullRoute(router, path)
    },
    [isLoggedIn, pathForNav, pathname, router]
  )

  useEffect(() => {
    if (!isLoggedIn) return
    // After first paint, off the critical path of whatever page just loaded. Safari has no
    // requestIdleCallback; fall back to a short timeout there.
    const idle = window.requestIdleCallback ?? ((cb: () => void) => window.setTimeout(cb, 1000))
    const cancelIdle = window.cancelIdleCallback ?? window.clearTimeout
    const handle = idle(
      () => {
        for (const path of Object.values(NAV_PATHS)) prefetchFullRoute(router, path)
      },
      { timeout: 2000 }
    )
    return () => cancelIdle(handle)
  }, [isLoggedIn, router])

  // From a full-page subroute, Trade is a real navigation too; warm the page it will open.
  useEffect(() => {
    if (!isLoggedIn || !isFullPageSubroute || !tradeTargetPageId) return
    prefetchFullRoute(router, getTradePagePath(tradeTargetPageId))
  }, [isFullPageSubroute, isLoggedIn, router, tradeTargetPageId])

  const openTradePage = useCallback(() => {
    setPendingNavId('trade')
    setCollapsed(true)
    if (tradeTargetPageId) {
      navigateToTradePage(tradeTargetPageId)
      return
    }
    router.push('/trade')
  }, [navigateToTradePage, router, setCollapsed, tradeTargetPageId])

  const items: AppSidePanelItem[] = [
    {
      id: 'home',
      label: tNav('home'),
      icon: HomeIcon,
      active: activeNavId === 'home',
      onIntent: () => warmNav('home'),
      onSelect: openHome,
    },
    {
      id: 'trade',
      label: 'Trade',
      icon: CandlesIcon,
      active: activeNavId === 'trade',
      onIntent: () => warmNav('trade'),
      onSelect: openTradePage,
    },
    {
      id: 'risk-slides',
      label: 'Risk Slides',
      icon: LayersIcon,
      active: activeNavId === 'risk-slides',
      onIntent: () => warmNav('risk-slides'),
      onSelect: openRiskSlides,
    },
    {
      id: 'delta-hedge',
      label: 'Delta Hedge',
      icon: TrendIcon,
      active: activeNavId === 'delta-hedge',
      onIntent: () => warmNav('delta-hedge'),
      onSelect: openDeltaHedge,
    },
    {
      id: 'screener',
      label: 'Screener',
      icon: RadarIcon,
      active: activeNavId === 'screener',
      onIntent: () => warmNav('screener'),
      onSelect: openScreener,
    },
    {
      id: 'arbitrage',
      label: 'Arbitrage',
      icon: ArbitrageIcon,
      active: activeNavId === 'arbitrage',
      onIntent: () => warmNav('arbitrage'),
      onSelect: openArbitrage,
    },
    // Maker RFQ blotter: hidden without ALGO_TYPE_RFQ (the backend drops the stream anyway).
    ...(canRfq
      ? [
          {
            id: 'rfq',
            label: tNav('rfq'),
            icon: RfqIcon,
            active: activeNavId === 'rfq',
            onIntent: () => warmNav('rfq'),
            onSelect: openRfqMaker,
          },
        ]
      : []),
    // Hidden on plans without the algo, so there is no dead entry.
    ...(showOptionsMm
      ? [
          {
            id: 'options-mm',
            label: tNav('optionsMm'),
            icon: ActivityIcon,
            active: activeNavId === 'options-mm',
            onIntent: () => warmNav('options-mm'),
            onSelect: openOptionsMm,
          },
        ]
      : []),
    {
      id: 'assistant',
      label: tAssistant('title'),
      icon: SparkleIcon,
      active: activeNavId === 'assistant',
      onIntent: () => warmNav('assistant'),
      indicator: assistantIndicator,
      onSelect: openAssistantPage,
    },
  ]

  const expandedContent = (
    <nav
      // px-2.5 + each row's pl-2.5 puts every icon 20px in, matching the collapsed rail
      // (its own px-2.5 + pl-2.5), so icons don't jump sideways when the panel opens.
      className={cn(
        'flex h-full min-h-0 flex-col gap-1.5 px-2.5',
        isAssistantPage ? 'overflow-hidden' : 'overflow-y-auto'
      )}
      aria-label={tNav('tradeMenu')}
    >
      <div
        className={cn(
          'flex flex-col gap-1.5',
          isAssistantPage && 'max-h-[45%] min-h-0 shrink-0 overflow-y-auto'
        )}
      >
        <button
          type="button"
          onClick={openHome}
          onPointerEnter={() => warmNav('home')}
          onFocus={() => warmNav('home')}
          aria-current={activeNavId === 'home' ? 'page' : undefined}
          className={cn(
            PRIMARY_NAV_ITEM_CLASS,
            activeNavId === 'home' && 'text-emphasis-foreground'
          )}
        >
          {activeNavId === 'home' && <ExpandedActiveFill />}
          <HomeIcon className="relative size-4 shrink-0" />
          <span className="relative min-w-0 flex-1 text-left">{tNav('home')}</span>
        </button>
        <button
          type="button"
          onClick={openTradePage}
          onPointerEnter={() => warmNav('trade')}
          onFocus={() => warmNav('trade')}
          aria-current={activeNavId === 'trade' ? 'page' : undefined}
          className={cn(
            PRIMARY_NAV_ITEM_CLASS,
            activeNavId === 'trade' && 'text-emphasis-foreground'
          )}
        >
          {activeNavId === 'trade' && <ExpandedActiveFill />}
          <CandlesIcon className="relative size-4 shrink-0" />
          <span className="relative min-w-0 flex-1 text-left">{tNav('trade')}</span>
        </button>
        <button
          type="button"
          onClick={openRiskSlides}
          onPointerEnter={() => warmNav('risk-slides')}
          onFocus={() => warmNav('risk-slides')}
          aria-current={activeNavId === 'risk-slides' ? 'page' : undefined}
          className={cn(
            PRIMARY_NAV_ITEM_CLASS,
            activeNavId === 'risk-slides' && 'text-emphasis-foreground'
          )}
        >
          {activeNavId === 'risk-slides' && <ExpandedActiveFill />}
          <LayersIcon className="relative size-4 shrink-0" />
          <span className="relative min-w-0 flex-1 text-left">{tNav('riskSlides')}</span>
        </button>
        <button
          type="button"
          onClick={openDeltaHedge}
          onPointerEnter={() => warmNav('delta-hedge')}
          onFocus={() => warmNav('delta-hedge')}
          aria-current={activeNavId === 'delta-hedge' ? 'page' : undefined}
          className={cn(
            PRIMARY_NAV_ITEM_CLASS,
            activeNavId === 'delta-hedge' && 'text-emphasis-foreground'
          )}
        >
          {activeNavId === 'delta-hedge' && <ExpandedActiveFill />}
          <TrendIcon className="relative size-4 shrink-0" />
          <span className="relative min-w-0 flex-1 text-left">{tNav('deltaHedge')}</span>
        </button>
        <button
          type="button"
          onClick={openScreener}
          onPointerEnter={() => warmNav('screener')}
          onFocus={() => warmNav('screener')}
          aria-current={activeNavId === 'screener' ? 'page' : undefined}
          className={cn(
            PRIMARY_NAV_ITEM_CLASS,
            activeNavId === 'screener' && 'text-emphasis-foreground'
          )}
        >
          {activeNavId === 'screener' && <ExpandedActiveFill />}
          <RadarIcon className="relative size-4 shrink-0" />
          <span className="relative min-w-0 flex-1 text-left">{tNav('screener')}</span>
        </button>
        <button
          type="button"
          onClick={openArbitrage}
          onPointerEnter={() => warmNav('arbitrage')}
          onFocus={() => warmNav('arbitrage')}
          aria-current={activeNavId === 'arbitrage' ? 'page' : undefined}
          className={cn(
            PRIMARY_NAV_ITEM_CLASS,
            activeNavId === 'arbitrage' && 'text-emphasis-foreground'
          )}
        >
          {activeNavId === 'arbitrage' && <ExpandedActiveFill />}
          <ArbitrageIcon className="relative size-4 shrink-0" />
          <span className="relative min-w-0 flex-1 text-left">{tNav('arbitrage')}</span>
        </button>
        {canRfq && (
          <button
            type="button"
            onClick={openRfqMaker}
            onPointerEnter={() => warmNav('rfq')}
            onFocus={() => warmNav('rfq')}
            aria-current={activeNavId === 'rfq' ? 'page' : undefined}
            className={cn(
              PRIMARY_NAV_ITEM_CLASS,
              activeNavId === 'rfq' && 'text-emphasis-foreground'
            )}
          >
            {activeNavId === 'rfq' && <ExpandedActiveFill />}
            <RfqIcon className="relative size-4 shrink-0" />
            <span className="relative min-w-0 flex-1 text-left">{tNav('rfq')}</span>
          </button>
        )}
        {showOptionsMm && (
          <button
            type="button"
            onClick={openOptionsMm}
            onPointerEnter={() => warmNav('options-mm')}
            onFocus={() => warmNav('options-mm')}
            aria-current={activeNavId === 'options-mm' ? 'page' : undefined}
            className={cn(
              PRIMARY_NAV_ITEM_CLASS,
              activeNavId === 'options-mm' && 'text-emphasis-foreground'
            )}
          >
            {activeNavId === 'options-mm' && <ExpandedActiveFill />}
            <ActivityIcon className="relative size-4 shrink-0" />
            <span className="relative min-w-0 flex-1 text-left">{tNav('optionsMm')}</span>
          </button>
        )}
        <button
          type="button"
          onClick={openAssistantPage}
          onPointerEnter={() => warmNav('assistant')}
          onFocus={() => warmNav('assistant')}
          aria-current={activeNavId === 'assistant' ? 'page' : undefined}
          className={cn(
            PRIMARY_NAV_ITEM_CLASS,
            activeNavId === 'assistant' && 'text-emphasis-foreground'
          )}
        >
          {activeNavId === 'assistant' && <ExpandedActiveFill />}
          <SparkleIcon className="relative size-4 shrink-0" />
          <span className="relative min-w-0 flex-1 text-left">{tAssistant('title')}</span>
          {assistantIndicator && (
            <span
              className="bg-ox-cyan size-1.5 shrink-0 rounded-full"
              aria-label={assistantIndicator}
            />
          )}
        </button>
      </div>
      {isAssistantPage && (
        // The page's own navigation, under its nav item: New chat, then the history, which
        // takes the rest of the height and scrolls on its own.
        <div className="border-foreground/10 mt-2 flex min-h-0 flex-1 flex-col border-t pt-2">
          <button
            type="button"
            onClick={() => openAssistant(store, { thread: { kind: 'new' } })}
            className={PRIMARY_NAV_ITEM_CLASS}
          >
            <SquarePenIcon className="size-4 shrink-0" />
            <span className="min-w-0 flex-1 text-left">{tAssistant('newChat')}</span>
          </button>
          <p className="text-foreground/40 px-2.5 pt-3 pb-1.5 text-[11px]">
            {tAssistant('recent')}
          </p>
          <AssistantRecentList className="-mx-1.5 min-h-0 flex-1" />
        </div>
      )}
    </nav>
  )

  // Grabbing the panel's right border and dragging it live-drives both the visual width AND
  // the backing atoms as the hidden/collapsed boundary is crossed. Border-drag only ever
  // operates within the 0-40px range — going from collapsed to hidden and back, never into
  // expanded — so it's clamped to PANEL_MAX_RESIZE_WIDTH_PX regardless of how far right the
  // cursor moves. The expanded panel is only ever reached (and left) by clicking, since its
  // nav content isn't designed to be fluidly resized. Grabbing the border while already
  // expanded still works: the drag immediately clamps down to that 40px ceiling, then continues
  // shrinking toward hidden from there. Not a persisted resize — the panel has no adjustable
  // width otherwise, so on release the imperative width override clears and AppSidePanel's own
  // `w-56`/`w-12` classes (+ width transition) take over, already agreeing with whichever zone
  // the drag ended in.
  const panel = (
    <AppSidePanel
      ariaLabel="Trade navigation"
      collapsed={collapsed}
      onCollapsedChange={setCollapsed}
      items={items}
      expandedContent={expandedContent}
      showLogo
      // Figma "Rail v3": full height and borderless, on the same page gradient as everything
      // else (bg-page is pinned to the viewport, so it lines up seamlessly). Opened, it lifts
      // over the content with a shadow.
      className="bg-page !fixed inset-y-0 left-0 z-40 !flex !h-auto transition-[width,box-shadow] data-[collapsed=false]:shadow-(--shell-card-shadow)"
      footer={
        // Expanded: fixed at the panel's full width so labels are clipped, not re-truncated,
        // while the width animates.
        <div
          className={cn(
            'flex flex-col gap-1.5',
            !collapsed && 'w-56 px-2.5',
            !collapsed && isAssistantPage && 'max-h-[35dvh] overflow-y-auto [&>*]:shrink-0'
          )}
        >
          {/* Support and the connection status used to sit in the bottom status bar; they're
              shell-level, not page-level, so they live with the other shell controls here. */}
          <SidePanelSupport collapsed={collapsed} collapsedClassName={FOOTER_BUTTON_COLLAPSED} />
          {/* Reference, not a workspace page: the glossary is public and has no app shell, so
              it sits with Support rather than in the nav above. */}
          <MaybeRailTooltip label={tNav('glossary')} enabled={collapsed}>
            <Link
              href="/glossary"
              aria-label={tNav('glossary')}
              className={collapsed ? FOOTER_BUTTON_COLLAPSED : FOOTER_BUTTON_EXPANDED}
            >
              <BookOpenIcon className="text-orderx-button-mint size-4" />
              {!collapsed && <span className="whitespace-nowrap">{tNav('glossary')}</span>}
            </Link>
          </MaybeRailTooltip>
          <SidePanelConnectionStatus
            collapsed={collapsed}
            collapsedClassName={FOOTER_BUTTON_COLLAPSED}
            expandedClassName={FOOTER_BUTTON_EXPANDED}
          />
          {/* Tutorials sit with Settings rather than in the header, which is kept to the
              workspace's own controls. The ring fills as they're finished; once all are, the
              entry leaves (Settings → General still opens them). Hidden until the saved
              progress loads, so a finished user doesn't see it flash. */}
          {tutorials.ready && !tutorials.complete ? (
            <MaybeRailTooltip label={tutorialsLabel} enabled={collapsed}>
              <button
                type="button"
                data-tour="tutorials"
                aria-label={tutorialsLabel}
                onClick={() => setTutorialHubOpen(true)}
                className={collapsed ? FOOTER_BUTTON_COLLAPSED : FOOTER_BUTTON_EXPANDED}
              >
                <TutorialProgressRing
                  done={tutorials.done}
                  total={tutorials.total}
                  className="text-orderx-button-mint size-4"
                />
                {!collapsed && (
                  <>
                    <span className="whitespace-nowrap">{tTutorialHub('open')}</span>
                    <span className="text-foreground/50 ml-auto text-xs tabular-nums">
                      {tutorialPercent}%
                    </span>
                  </>
                )}
              </button>
            </MaybeRailTooltip>
          ) : null}
          <MaybeRailTooltip label={tNav('settings')} enabled={collapsed}>
            <button
              type="button"
              aria-label={tNav('settings')}
              onClick={() => setSettingOpen(true)}
              className={collapsed ? FOOTER_BUTTON_COLLAPSED : FOOTER_BUTTON_EXPANDED}
            >
              <SettingsRingIcon className="text-orderx-button-mint" />
              {!collapsed && <span className="whitespace-nowrap">{tNav('settings')}</span>}
            </button>
          </MaybeRailTooltip>
          <SidebarAccountMenu collapsed={collapsed} email={user?.email} onLogout={handleLogout} />
        </div>
      }
    />
  )

  return (
    <>
      {canUseBrowser && !isMobileLayout ? createPortal(panel, document.body) : null}
      <SettingDialog
        open={settingOpen}
        onOpenChange={setSettingOpen}
        defaultTab={SettingTab.GENERAL}
      >
        {null}
      </SettingDialog>
    </>
  )
}
